import { useAuthStore } from './authStore'

/**
 * Hook to check user permissions
 */
export const usePermissions = () => {
  const { user } = useAuthStore()

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    if (!user) return false

    // Check if user has platform_admin, org_admin, or admin role
    const hasAdminRole = user.roles?.some(
      (role: any) => role.name === 'platform_admin' || role.name === 'org_admin' || role.name === 'admin'
    )
    if (hasAdminRole) {
      return true
    }

    // Check if user has the specific permission by name
    return user.permissions?.some((p: any) => p.name === permission) || false
  }

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user) {
      return false
    }

    // Check if user has platform_admin, org_admin, or admin role
    const hasAdminRole = user.roles?.some(
      (role: any) => role.name === 'platform_admin' || role.name === 'org_admin' || role.name === 'admin'
    )
    if (hasAdminRole) {
      return true
    }

    const result = permissions.some((permission) =>
      user.permissions?.some((p: any) => p.name === permission)
    )
    return result
  }

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!user) return false

    // Check if user has platform_admin, org_admin, or admin role
    const hasAdminRole = user.roles?.some(
      (role: any) => role.name === 'platform_admin' || role.name === 'org_admin' || role.name === 'admin'
    )
    if (hasAdminRole) {
      return true
    }

    return permissions.every((permission) =>
      user.permissions?.some((p: any) => p.name === permission)
    )
  }

  /**
   * Check if user's role level meets a minimum threshold
   * Lower numbers = higher authority
   */
  const hasRoleLevel = (maxLevel: number): boolean => {
    if (!user) return false

    // Check if user has platform_admin, org_admin, or admin role
    const hasAdminRole = user.roles?.some(
      (role: any) => role.name === 'platform_admin' || role.name === 'org_admin' || role.name === 'admin'
    )
    if (hasAdminRole) {
      return true
    }

    // Get the lowest role_level (highest authority) from user's roles
    const minRoleLevel = user.roles?.reduce(
      (min: number, role: any) => Math.min(min, role.role_level || 999),
      999
    )

    if (minRoleLevel === undefined || minRoleLevel === 999) return false
    return minRoleLevel <= maxLevel
  }

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    return user?.roles?.some((r: any) => r.name === role) || false
  }

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => user?.roles?.some((r: any) => r.name === role))
  }

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRoleLevel,
    hasRole,
    hasAnyRole,
    user,
  }
}

/**
 * Permission constants for common checks
 */
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard:view:all',

  // Projects
  PROJECTS_CREATE: 'projects:create:all',
  PROJECTS_READ: 'projects:read:all',
  PROJECTS_UPDATE: 'projects:update:all',
  PROJECTS_DELETE: 'projects:delete:all',

  // Test Cases
  TESTCASES_CREATE: 'testcases:create:all',
  TESTCASES_READ: 'testcases:read:all',
  TESTCASES_UPDATE: 'testcases:update:all',
  TESTCASES_DELETE: 'testcases:delete:all',

  // Test Plans
  TESTPLANS_CREATE: 'testplans:create:all',
  TESTPLANS_READ: 'testplans:read:all',
  TESTPLANS_UPDATE: 'testplans:update:all',
  TESTPLANS_DELETE: 'testplans:delete:all',

  // Builds
  BUILDS_CREATE: 'builds:create:all',
  BUILDS_READ: 'builds:read:all',
  BUILDS_UPDATE: 'builds:update:all',
  BUILDS_DELETE: 'builds:delete:all',

  // Executions
  EXECUTIONS_CREATE: 'executions:create:all',
  EXECUTIONS_READ: 'executions:read:all',
  EXECUTIONS_UPDATE: 'executions:update:all',
  EXECUTIONS_DELETE: 'executions:delete:all',

  // Requirements
  REQUIREMENTS_CREATE: 'requirements:create:all',
  REQUIREMENTS_READ: 'requirements:read:all',
  REQUIREMENTS_UPDATE: 'requirements:update:all',
  REQUIREMENTS_DELETE: 'requirements:delete:all',

  // Defects
  DEFECTS_CREATE: 'defects:create:all',
  DEFECTS_READ: 'defects:read:all',
  DEFECTS_UPDATE: 'defects:update:all',
  DEFECTS_DELETE: 'defects:delete:all',

  // Reports
  REPORTS_VIEW: 'reports:view:all',
  REPORTS_CREATE: 'reports:create:all',
  REPORTS_EXPORT: 'reports:export:all',

  // Audit (Leadership only)
  AUDIT_VIEW: 'audit:view:all',
  AUDIT_VIEW_DEPARTMENT: 'audit:view:department',
  AUDIT_VIEW_TEAM: 'audit:view:team',
  AUDIT_EXPORT: 'audit:export:all',

  // Team Management
  TEAM_VIEW: 'team:view:all',
  TEAM_INVITE: 'team:invite:all',
  TEAM_UPDATE: 'team:update:all',
  TEAM_REMOVE: 'team:remove:all',

  // Organization
  ORGANIZATION_VIEW: 'organization:view:all',
  ORGANIZATION_UPDATE: 'organization:update:all',
  BILLING_VIEW: 'billing:view:all',
  BILLING_MANAGE: 'billing:manage:all',
  INTEGRATIONS_VIEW: 'integrations:view:all',
  INTEGRATIONS_MANAGE: 'integrations:manage:all',
} as const

/**
 * Role level constants
 * Lower = higher authority
 */
export const ROLE_LEVELS = {
  ORG_ADMIN: 10,
  ADMIN: 15,
  C_SUITE: 20,
  EXECUTIVE: 30,
  DIRECTOR: 40,
  MANAGER: 50,
  TEST_MANAGER: 55,
  ENGINEER: 60,
  TESTER: 65,
  VIEWER: 70,
} as const
